import { EventIds } from 'constants/eventIds';
import { CreatorBpmMultiplier } from 'controllers/business/Creator/Creator.types';
import { log } from 'utils/log';
import { CreatorSession } from './CreatorSession';
import { CreatorTrackConfigType } from './CreatorSession.types';
import {
    getTimeFactor,
    getKeyAsSemitone,
    getSemitoneShift,
    getKeyOffset,
    getScaledDuration,
    getPeakAmplitude,
    getRecommendedOffsets,
    getDurationInBars,
    getDyadicBarsLengthInSamples,
    getDyadicBarsLengthInSeconds,
} from './CreatorSession.utils';

export class CreatorSessionTrack {
    // PROPERTIES
    private _id: string;
    private _useLegacyProcessing: boolean;

    get id(): string {
        return this._id;
    }

    private _isUIReady = false;
    get isUIReady(): boolean {
        return this._isUIReady;
    }
    public setUIReady() {
        this._isUIReady = true;
    }

    private _duration: number;
    get duration(): number {
        return this._duration;
    }

    private _scaledDuration: number;
    get scaledDuration(): number {
        return this._scaledDuration;
    }

    get recommendedValues(): number[] {
        return this._offset.recommendedValues;
    }

    private _keyAsSemitone: number | null;
    private _offset = { base: 0, track: 0, recommendedValues: [0] };

    get scaledLengthInSamples(): number {
        return this.warpBuffer.getChannelData(0).length;
    }

    private _pitchTranspose: number | undefined;
    get pitchTranspose(): number | undefined {
        return this._pitchTranspose;
    }

    private _semitoneShift = 0;
    get semitoneShift(): number {
        return this._semitoneShift;
    }

    private _bpm: number;
    get bpm(): number {
        return this._bpm;
    }

    private _bpmMultiplier: CreatorBpmMultiplier;
    get bpmMultiplier(): CreatorBpmMultiplier {
        return this._bpmMultiplier;
    }

    private _timeFactor = 1;
    get timeFactor(): number {
        return this._timeFactor;
    }

    private _timeFactorBeforeMultiplier = 1;
    get timeFactorBeforeMultiplier(): number {
        return this._timeFactorBeforeMultiplier;
    }

    private _gain = 1;
    get gain(): number {
        return this._gain;
    }

    private _gainScale = 1;

    get calculatedGain(): number {
        return this._gain * this._gainScale;
    }

    private _stream: Response;
    private _session: CreatorSession;

    private _isPlaybackReady = false;
    get isPlaybackReady(): boolean {
        return this._isPlaybackReady;
    }

    // Gets updated each time a loop play restarts, used to update the waveform
    public loopStartedAt = 0;

    private _peakAmplitude = 0;
    get peakAmplitude(): number {
        return this._peakAmplitude;
    }

    private _warpBuffer: AudioBuffer;
    get warpBuffer(): AudioBuffer {
        return this._warpBuffer;
    }

    // Only used for legacy processing
    public activeBufferNode: AudioBufferSourceNode | undefined;
    public staleBufferNode: AudioBufferSourceNode | undefined;

    private _sharedBufferNode: AudioWorkletNode | undefined;

    private _processorNode: AbstractAudioProcessorWorker | undefined;
    private _gainNode: GainNode;
    get gainNode(): GainNode {
        return this._gainNode;
    }

    private _onTrackReady: () => void;

    // CONSTRUCTOR
    constructor({
        id,
        config,
        session,
        onTrackReady,
        useLegacyProcessing = true,
    }: {
        id: string;
        config: CreatorTrackConfigType;
        session: CreatorSession;
        onTrackReady: () => void;
        useLegacyProcessing?: boolean;
    }) {
        this._id = id;
        this._useLegacyProcessing = useLegacyProcessing;
        this._bpm = config.bpm;
        this._bpmMultiplier = config.bpmMultiplier;
        this._gain = config.gain;
        this._keyAsSemitone = getKeyAsSemitone(
            config.key?.root,
            config.key?.quality,
        );
        this._pitchTranspose = config.pitchTranspose;
        this._duration = config.duration ? config.duration : 0;
        this._scaledDuration = config.duration ? config.duration : 0;
        this._stream = config.stream;

        this._session = session;
        this._onTrackReady = onTrackReady;
        this._warpBuffer = this._session.audioContext.createBuffer(
            2,
            1,
            this._session.audioContext.sampleRate,
        );
        this._gainNode = this._session.audioContext.createGain();
    }

    // METHODS
    public startAt = (startAt: number) => {
        this._sharedBufferNode?.port.postMessage({
            startAt,
        });
    };

    public stopAt = (stopAt: number) => {
        this._sharedBufferNode?.port.postMessage({
            stopAt,
        });
    };

    public stopNow = () => {
        this._sharedBufferNode?.port.postMessage({
            stopNow: true,
        });
    };

    private createWarpNode =
        async (): Promise<AbstractAudioProcessorWorker> => {
            const { audioContext } = this._session;

            const warpWorker = this._useLegacyProcessing
                ? new Worker(
                      `${import.meta.env.BASE_URL}workers/warpWorkerLegacy.js`,
                  )
                : new Worker(
                      `${import.meta.env.BASE_URL}workers/warpWorker.js`,
                  );

            warpWorker.onmessage = this._useLegacyProcessing
                ? this.handleWarpNodeMessageLegacy
                : this.handleWarpNodeMessage;

            const warpNode = {
                worker: warpWorker,
                sendMessage: (message: unknown): void => {
                    warpWorker.postMessage(message);
                },
            };

            warpNode.sendMessage({
                sampleRate: audioContext.sampleRate,
            });

            return warpNode;
        };

    private _createSharedBufferNode(
        sharedBuffers: [SharedArrayBuffer, SharedArrayBuffer],
    ): void {
        if (this._sharedBufferNode) {
            this._sharedBufferNode.disconnect();
        }

        this._sharedBufferNode = new AudioWorkletNode(
            this._session.audioContext,
            'SharedBufferNode',
            { numberOfOutputs: 1, outputChannelCount: [2] },
        );

        this._sharedBufferNode.connect(this._gainNode);

        this._sharedBufferNode.port.postMessage({
            sharedBuffers,
        });
    }

    private handleWarpNodeMessageLegacy = (messageEvent: {
        readonly data: unknown;
    }): void => {
        const message = (messageEvent as any).data;

        if (message.processedBuffers) {
            const { length } = message.processedBuffers.left;

            this._warpBuffer = this._session.audioContext.createBuffer(
                2,
                length,
                this._session.audioContext.sampleRate,
            );

            const left = this._warpBuffer.getChannelData(0);
            const right = this._warpBuffer.getChannelData(1);

            left.set(message.processedBuffers.left.subarray());
            right.set(message.processedBuffers.right.subarray());
        }

        if (typeof message.playbackReady === 'boolean') {
            this._isPlaybackReady = message.playbackReady;

            if (message.playbackReady) {
                this._onTrackReady();
            }
        }

        if (message.processedFramesCount) {
            this._scaledDuration =
                message.processedFramesCount /
                this._session.audioContext.sampleRate;
        }

        if (message.error) {
            log.error(
                'An error occurred in the warpWorker',
                EventIds.WarpWorkerError,
                message.error,
            );
        }
    };

    private handleWarpNodeMessage = (messageEvent: {
        readonly data: unknown;
    }): void => {
        const message = (messageEvent as any).data;

        if (message.sharedBuffers) {
            (window as any).sharedBuffers = message.sharedBuffers;
            this._createSharedBufferNode(message.sharedBuffers);
        }

        if (typeof message.playbackReady === 'boolean') {
            this._isPlaybackReady = message.playbackReady;

            if (message.playbackReady) {
                this._onTrackReady();
            }

            this._sharedBufferNode?.port.postMessage({
                startPlaying: message.playbackReady,
            });
        }

        if (message.processedFramesCount) {
            this._scaledDuration =
                message.processedFramesCount /
                this._session.audioContext.sampleRate;
        }

        if (message.error) {
            log.error(
                'An error occurred in the warpWorker',
                EventIds.WarpWorkerError,
                message.error,
            );
        }
    };

    public resetStartedAt(): void {
        this.loopStartedAt = 0;
    }

    // Safari does not support the Promise-based decodeAudioData so we shim it
    private decodeAudioData = async (
        buffer: ArrayBuffer,
    ): Promise<AudioBuffer> => {
        return new Promise((resolve) => {
            this._session.audioContext.decodeAudioData(
                buffer,
                (decodedBuffer) => resolve(decodedBuffer),
            );
        });
    };

    private blobToArrayBuffer = async (
        blob: Blob | Response,
    ): Promise<ArrayBuffer> => {
        if ('arrayBuffer' in blob) return blob.arrayBuffer();

        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => resolve(reader.result as ArrayBuffer);
            reader.onerror = () => reject;
            reader.readAsArrayBuffer(blob);
        });
    };

    public init = async ({
        baseBpm,
        baseKeyAsSemitone,
    }: {
        baseBpm: number;
        baseKeyAsSemitone: number;
    }): Promise<void> => {
        const buffer = await this.blobToArrayBuffer(this._stream);

        const decodedBuffer = await this.decodeAudioData(buffer);

        const warpNode: AbstractAudioProcessorWorker =
            await this.createWarpNode();

        // buffer duration rather than sample metadata because it's more accurate for calculation
        const { duration: bufferDuration } = decodedBuffer;

        this._duration = bufferDuration;

        this._gainNode.connect(this._session.masterGainNode);

        this._handleGainUpdate();

        const leftBuffer = decodedBuffer.getChannelData(0);
        let rightBuffer: Float32Array;

        // some samples are in Mono and only have left channel data
        if (decodedBuffer.numberOfChannels === 2) {
            rightBuffer = decodedBuffer.getChannelData(1);
        } else {
            rightBuffer = decodedBuffer.getChannelData(0);
        }

        warpNode.sendMessage({
            leftBuffer,
            rightBuffer,
        });

        this._peakAmplitude = getPeakAmplitude(leftBuffer, rightBuffer);

        [
            this._timeFactor,
            this._bpmMultiplier,
            this._timeFactorBeforeMultiplier,
        ] = getTimeFactor(baseBpm, this._bpm, this._bpmMultiplier);

        this._scaledDuration = getScaledDuration(
            this._duration,
            this._timeFactor,
        );

        this._processorNode = warpNode;

        // PITCH / PS
        this._updateBaseOffset(baseKeyAsSemitone);
        this._updateTrackOffset();
        this._updatePitch(baseKeyAsSemitone);

        // BPM / TS
        this._updateBpm(baseBpm);
        this.updateNodeStretchParams();
    };

    public remove = (): void => {
        this._processorNode?.sendMessage({
            destroy: true,
        });
        this._processorNode?.worker.terminate();
        this._gainNode.disconnect();
    };

    public getScaledDurationInBars = (sessionBpm: number): number => {
        return getDurationInBars(this._scaledDuration, sessionBpm);
    };

    /**
     * Get the number of samples needed to playback the time-scaled buffer,
     * clamped to the nearest dyadic (2's power) interval.
     *
     * @return integer - samples
     */

    public getScaledDyadicLengthInSamples = (
        sessionBpm: number,
        sampleRate: number,
    ): number => {
        return getDyadicBarsLengthInSamples(
            this.getScaledDurationInBars(sessionBpm),
            sessionBpm,
            sampleRate,
        );
    };

    /**
     * Get the number of seconds needed to playback the time-scaled buffer,
     * clamped to the nearest dyadic (2's power) interval.
     * @see {module:CreatorSessionUtils.clampToDyadicInterval}
     * @return integer - samples
     */

    public getScaledDyadicDurationInSeconds = (sessionBpm: number): number => {
        return getDyadicBarsLengthInSeconds(
            this.getScaledDurationInBars(sessionBpm),
            sessionBpm,
        );
    };

    private updateNodeStretchParams = (): void => {
        this._isPlaybackReady = false;

        this._processorNode?.sendMessage({
            pitchShift: this._semitoneShift,
            rate: this._timeFactor,
        });
    };

    private _updateBaseOffset = (baseKeyAsSemitone?: number): void => {
        if (this._keyAsSemitone !== null && baseKeyAsSemitone !== undefined) {
            this._offset.base = getKeyOffset(
                baseKeyAsSemitone,
                this._keyAsSemitone,
            );

            this._offset.recommendedValues = getRecommendedOffsets(
                this._offset.base,
            );
        }
    };

    private _updateTrackOffset = (): void => {
        if (this._pitchTranspose === undefined) {
            this._pitchTranspose = this._offset.base;
        }
        this._offset.track = this._pitchTranspose - this._offset.base;
    };

    private _updatePitch = (
        baseKeyAsSemitone?: number,
        pitchTranspose?: number,
    ): void => {
        // Update Session / Track base offset
        this._updateBaseOffset(baseKeyAsSemitone);

        // Overwrite with user knob position
        if (typeof pitchTranspose !== 'undefined') {
            this._pitchTranspose = pitchTranspose;
            this._updateTrackOffset();
        }

        this._pitchTranspose = this._offset.base + this._offset.track;

        this._semitoneShift = getSemitoneShift(this._pitchTranspose);
    };

    public updatePitch = ({
        baseKeyAsSemitone,
        pitchTranspose,
    }: {
        baseKeyAsSemitone?: number;
        pitchTranspose?: number;
    }): void => {
        this._updatePitch(baseKeyAsSemitone, pitchTranspose);
        this.updateNodeStretchParams();
    };

    private _updateBpm = (
        baseBpm: number,
        bpm?: number,
        bpmMultiplier?: CreatorBpmMultiplier,
    ): void => {
        if (typeof bpm !== 'undefined') {
            this._bpm = bpm;
        }
        if (typeof bpmMultiplier !== 'undefined') {
            this._bpmMultiplier = bpmMultiplier;
        }

        [
            this._timeFactor,
            this._bpmMultiplier,
            this._timeFactorBeforeMultiplier,
        ] = getTimeFactor(baseBpm, this._bpm, this._bpmMultiplier);

        this._scaledDuration = getScaledDuration(
            this._duration,
            this._timeFactor,
        );
    };

    public updateBpm = ({
        baseBpm,
        bpm,
        bpmMultiplier,
    }: {
        baseBpm: number;
        bpm?: number;
        bpmMultiplier?: CreatorBpmMultiplier;
    }): void => {
        this._updateBpm(baseBpm, bpm, bpmMultiplier);
        this.updateNodeStretchParams();
    };

    private _handleGainUpdate = (): void => {
        this._gainNode.gain.linearRampToValueAtTime(
            this._gain * this._gainScale,
            this._session.audioContext.currentTime,
        );
    };

    public updateGain = (gain: number): void => {
        this._gain = gain;
        this._handleGainUpdate();
    };

    public updateGainScale = (gainScale: number): void => {
        this._gainScale = gainScale;
        this._handleGainUpdate();
    };
}

abstract class AbstractAudioProcessorWorker {
    abstract worker: Worker;
    abstract sendMessage(message: unknown): void;
}
